import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Assessment() {
    const [structure, setStructure] = useState(null);
    const [answers, setAnswers] = useState({});
    const [catIdx, setCatIdx] = useState(0);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/assessment/questions").then((r) => setStructure(r.data));
    }, []);

    const flat = useMemo(() => {
        if (!structure) return [];
        return structure.categories.flatMap((c) =>
            c.questions.map((q) => ({ ...q, category: c.key, categoryLabel: c.label }))
        );
    }, [structure]);

    const answered = flat.filter((q) => {
        const v = answers[q.id];
        if (q.type === "reflection") return true; // optional
        return v !== undefined && v !== null && v !== "";
    }).length;
    const progressPct = flat.length ? (answered / flat.length) * 100 : 0;

    if (!structure) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Preparing the garden…
            </div>
        );
    }

    const category = structure.categories[catIdx];
    const isLast = catIdx === structure.categories.length - 1;

    const setAnswer = (qid, v) => setAnswers((prev) => ({ ...prev, [qid]: v }));

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
            };
            await api.post("/assessment/submit", payload);
            navigate("/dashboard?welcome=1");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground grain">
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-20 relative z-[2]">
                <div className="mb-10">
                    <div className="flex items-center justify-between mb-4">
                        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                            The Eden Blueprint
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Category {catIdx + 1} of {structure.categories.length}
                        </div>
                    </div>
                    <Progress value={progressPct} className="h-[2px] bg-foreground/10" data-testid="assessment-progress" />
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={category.key}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="mb-12">
                            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--eden-gold))] mb-4">
                                Garden of the {category.label}
                            </div>
                            <h1 className="font-serif text-5xl md:text-7xl leading-[1.05]">{category.label}</h1>
                            <p className="mt-6 text-muted-foreground text-lg max-w-2xl leading-relaxed">
                                {category.description}
                            </p>
                        </div>

                        <div className="space-y-10">
                            {category.questions.map((q, i) => (
                                <motion.div
                                    key={q.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className="border-t border-foreground/10 pt-8"
                                    data-testid={`question-${q.id}`}
                                >
                                    <div className="flex items-baseline gap-4 mb-6">
                                        <span className="text-xs text-muted-foreground tracking-widest">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <p className="font-serif text-2xl md:text-3xl leading-snug flex-1">{q.text}</p>
                                    </div>

                                    {q.type === "scale" && (
                                        <div className="pl-10">
                                            <Slider
                                                value={[answers[q.id] ?? 3]}
                                                onValueChange={(v) => setAnswer(q.id, v[0])}
                                                min={q.min}
                                                max={q.max}
                                                step={1}
                                                data-testid={`slider-${q.id}`}
                                                className="max-w-2xl"
                                            />
                                            <div className="flex justify-between max-w-2xl mt-3 text-xs text-muted-foreground">
                                                {q.labels.map((l) => (
                                                    <span key={l}>{l}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {q.type === "mcq" && (
                                        <div className="pl-10 flex flex-col gap-2 max-w-xl">
                                            {q.options.map((opt) => {
                                                const active = answers[q.id] === opt;
                                                return (
                                                    <button
                                                        key={opt}
                                                        onClick={() => setAnswer(q.id, opt)}
                                                        data-testid={`mcq-${q.id}-${opt.slice(0, 8)}`}
                                                        className={`text-left px-5 py-3 rounded-md border transition-colors ${
                                                            active
                                                                ? "bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] border-[hsl(var(--eden-forest))]"
                                                                : "border-foreground/15 hover:border-foreground/40"
                                                        }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {q.type === "reflection" && (
                                        <div className="pl-10 max-w-2xl">
                                            <Textarea
                                                value={answers[q.id] || ""}
                                                onChange={(e) => setAnswer(q.id, e.target.value)}
                                                placeholder={q.placeholder || "A short honest reflection…"}
                                                rows={3}
                                                data-testid={`reflection-${q.id}`}
                                                className="rounded-md border-foreground/15 bg-transparent resize-none"
                                            />
                                            <div className="text-xs text-muted-foreground mt-2">Optional</div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-16 flex items-center justify-between">
                            <Button
                                variant="ghost"
                                onClick={() => setCatIdx((i) => Math.max(0, i - 1))}
                                disabled={catIdx === 0}
                                data-testid="assessment-prev"
                            >
                                <ChevronLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back
                            </Button>
                            {isLast ? (
                                <Button
                                    onClick={submit}
                                    disabled={busy}
                                    data-testid="assessment-submit"
                                    className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 h-12 px-8 rounded-md"
                                >
                                    {busy ? "Cultivating your blueprint…" : "See my Eden Blueprint"}
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => { setCatIdx((i) => i + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                    data-testid="assessment-next"
                                    className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 h-12 px-8 rounded-md"
                                >
                                    Continue <ChevronRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                                </Button>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
